'use client';

import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import FormFieldRenderer from '@/components/ui/FormFieldRenderer';
import FormAlert from '@/components/ui/FormAlert';
import Text from '@/components/ui/Text';
import { COPY } from '@/lib/copy';
import { submitContactForm } from '@/lib/contact/submitContactForm';
import type { ContactFormData, FormField } from '@/lib/contentful/types';

type FormValues = Record<string, string>;

const ContactForm = ({ config, fields }: ContactFormData) => {
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const methods = useForm<FormValues>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: Object.fromEntries(fields.map((f) => [f.name, ''])),
  });

  const onSubmit = methods.handleSubmit(async (values) => {
    setSubmitError(null);
    setIsSuccess(false);

    try {
      const name = pickByValidationType(values, fields, 'name');
      const email = pickByValidationType(values, fields, 'email');
      const phone = pickByValidationType(values, fields, 'phone');
      const message = pickMessage(values, fields);

      await submitContactForm({ name, email, phone, message });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).dataLayer?.push({
        event: 'generate_lead',
        form: 'contact',
      });
      methods.reset();
      setIsSuccess(true);
    } catch {
      setSubmitError(COPY.contact.form.error);
    }
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-6">
        {config?.title && (
          <h3 className="font-serif text-[1.3rem] font-normal italic text-text pb-3 border-b border-primary/20">
            {config.title}
          </h3>
        )}
        {config?.subtitle && (
          <p className="font-sans text-[0.82rem] text-color-text-light leading-relaxed -mt-2">
            {config.subtitle}
          </p>
        )}

        <div className="flex flex-col gap-5">
          {fields.map((field) => (
            <FormFieldRenderer
              key={field.name}
              field={field}
              validationMessages={COPY.validation}
            />
          ))}
        </div>

        {config?.consentText && (
          <Text
            as="p"
            variant="formConsent"
            className="text-[0.7rem] text-color-text-light leading-relaxed"
          >
            {config.consentText}
          </Text>
        )}

        <button
          type="submit"
          disabled={
            methods.formState.isSubmitting || !methods.formState.isValid
          }
          className="w-full bg-primary hover:bg-color-primary-dark text-white font-sans text-[0.72rem] font-normal tracking-[0.2em] uppercase p-4 mt-[1.4rem] cursor-pointer transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {methods.formState.isSubmitting
            ? COPY.contact.form.submitting
            : COPY.contact.form.submit}
        </button>

        {isSuccess && (
          <FormAlert type="success" message={COPY.contact.form.success} />
        )}
        {submitError && <FormAlert type="error" message={submitError} />}
      </form>
    </FormProvider>
  );
};

export default ContactForm;

function pickByValidationType(
  values: Record<string, string>,
  fields: FormField[],
  type: FormField['validationType'],
) {
  const field = fields.find((f) => f.validationType === type);
  return field ? (values[field.name] ?? '').trim() : '';
}

function pickMessage(values: Record<string, string>, fields: FormField[]) {
  const field = fields.find((f) => f.inputType === 'textarea');
  return field ? (values[field.name] ?? '').trim() : '';
}
